import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  HStack,
  Text,
  Badge,
  Divider,
  useToast,
} from '@chakra-ui/react';
import { countRecipients } from '../../api/auth/mail';

const RecipientSelectionStep = ({ initialData, onComplete }) => {
  const toast = useToast();
  const [membershipFilter, setMembershipFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [keyword, setKeyword] = useState('');
  const [periodOption, setPeriodOption] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [directEmails, setDirectEmails] = useState('');
  const [filterCount, setFilterCount] = useState(0);
  const [directCount, setDirectCount] = useState(0);
  const [isCountLoading, setIsCountLoading] = useState(false);

  const handleCountFilter = async () => {
    setIsCountLoading(true);
    try {
      const filter = {};
      if (membershipFilter !== 'all')
        filter.membershipList = [parseInt(membershipFilter)];
      if (serviceFilter !== 'all') filter.serviceList = [serviceFilter];
      if (keyword) filter.keyword = keyword;
      if (periodOption && startDate && endDate) {
        filter.periodOption = periodOption;
        filter.startDate = startDate;
        filter.endDate = endDate;
      }
      const response = await countRecipients(filter);
      console.log(response);
      setFilterCount(response.count || 0);
      toast({
        title: '카운트 완료',
        description: `${response.count}명`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error(error);
      toast({
        title: '카운트 실패',
        description: '회원 수 조회 중 오류가 발생했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsCountLoading(false);
    }
  };

  const handleCountDirect = () => {
    const emails = directEmails
      .split(/[\,\n]/)
      .map(e => e.trim())
      .filter(e => e && e.includes('@'));
    setDirectCount(emails.length);
    toast({
      title: '카운트 완료',
      description: `${emails.length}명`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleNext = () => {
    const emails = directEmails
      .split(/[\,\n]/)
      .map(e => e.trim())
      .filter(e => e && e.includes('@'));
    const filter = {};
    if (membershipFilter !== 'all')
      filter.membershipList = [parseInt(membershipFilter)];
    if (serviceFilter !== 'all') filter.serviceList = [serviceFilter];
    if (keyword) filter.keyword = keyword;
    if (periodOption && startDate && endDate) {
      filter.periodOption = periodOption;
      filter.startDate = startDate;
      filter.endDate = endDate;
    }
    const hasFilter = Object.keys(filter).length > 0;
    onComplete({
      filter: hasFilter ? filter : null,
      recipients: emails,
      count: filterCount + directCount,
    });
  };

  const totalCount = filterCount + directCount;

  return (
    <VStack spacing={6} align="stretch">
      <Box>
        <Text fontSize="lg" fontWeight="bold" mb={4}>
          방법 A: 필터로 기존 회원 선택
        </Text>
        <VStack spacing={4} align="stretch">
          <HStack>
            <FormControl>
              <FormLabel>멤버십</FormLabel>
              <Select
                value={membershipFilter}
                onChange={e => setMembershipFilter(e.target.value)}
              >
                <option value="all">전체</option>
                <option value="0">무료</option>
                <option value="1">플랜 1</option>
                <option value="2">플랜 2</option>
                <option value="3">플랜 3</option>
                <option value="4">플랜 4</option>
                <option value="5">플랜 5</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>결제사</FormLabel>
              <Select
                value={serviceFilter}
                onChange={e => setServiceFilter(e.target.value)}
              >
                <option value="all">전체</option>
                <option value="firebase">Firebase</option>
                <option value="innopay">Innopay</option>
                <option value="iamport">Iamport</option>
                <option value="none">없음</option>
              </Select>
            </FormControl>
          </HStack>
          <FormControl>
            <FormLabel>검색 (이름/이메일)</FormLabel>
            <Input
              placeholder="검색어 입력"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
            />
          </FormControl>
          <HStack>
            <FormControl>
              <FormLabel>기간 옵션</FormLabel>
              <Select
                value={periodOption}
                onChange={e => setPeriodOption(e.target.value)}
              >
                <option value="">선택 안함</option>
                <option value="create_at">가입일</option>
                <option value="login_at">최근 로그인</option>
              </Select>
            </FormControl>
            {periodOption && (
              <>
                <FormControl>
                  <FormLabel>시작일</FormLabel>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>종료일</FormLabel>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                  />
                </FormControl>
              </>
            )}
          </HStack>
          <Button
            colorScheme="blue"
            onClick={handleCountFilter}
            isLoading={isCountLoading}
          >
            필터 대상 카운트
          </Button>
          {filterCount > 0 && (
            <Badge colorScheme="blue" fontSize="md" p={2}>
              필터 대상: {filterCount}명
            </Badge>
          )}
        </VStack>
      </Box>
      <Divider />
      <Box>
        <Text fontSize="lg" fontWeight="bold" mb={4}>
          방법 B: 이메일 직접 입력
        </Text>
        <VStack spacing={4} align="stretch">
          <FormControl>
            <FormLabel>이메일 주소 (쉼표 또는 줄바꿈으로 구분)</FormLabel>
            <Textarea
              placeholder="a@ex.com, b@ex.com\n c@ex.com"
              value={directEmails}
              onChange={e => setDirectEmails(e.target.value)}
              rows={6}
            />
          </FormControl>
          <Button colorScheme="teal" onClick={handleCountDirect}>
            직접 입력 대상 카운트
          </Button>
          {directCount > 0 && (
            <Badge colorScheme="teal" fontSize="md" p={2}>
              직접 입력 대상: {directCount}명
            </Badge>
          )}
        </VStack>
      </Box>
      <Divider />
      <Box>
        <HStack justify="space-between" align="center">
          <Text fontSize="xl" fontWeight="bold">
            총 대상: {totalCount}명
          </Text>
          <Button
            colorScheme="purple"
            size="lg"
            onClick={handleNext}
            isDisabled={totalCount === 0}
          >
            다음 단계
          </Button>
        </HStack>
      </Box>
    </VStack>
  );
};

export default RecipientSelectionStep;
